import React, { useEffect, useState } from "react";
import { EditIcon, HelpIcon, SaveIcon } from "assets/icons";
import Input from "components/BaseWidgets/form/input";
import Select from "components/BaseWidgets/select";
import { careerSelectOptions } from "components/PersonProfile/helper";
import Button from "components/BaseWidgets/button";
import { getCareerItems } from "./helper";
import "./style.scss";
import {
  useSelectOptions,
  useCourses,
  useProfession,
} from "hooks/api/useListing";
import { useIncomes } from "hooks/api/useListing";
import { mapToSelectOptions } from "utils/formatHelper";
import { selectEmployment } from "components/Onboard/components/helper";
import { displayTextOrDash } from "utils/formatHelper";
import { useEditProfile } from "hooks/api/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import Tooltip from "components/BaseWidgets/toolTip";

const employmentTypeMap = {
  1: "Part Time",
  2: "Full Time",
};

function CareerPanel({ isEdit, id, careerDetails, isLock, blurredField = [] }) {
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();

  const careerData = careerDetails?.career || {};

  const careerItems = getCareerItems(careerData);

  const [formData, setFormData] = useState(() =>
    Object.fromEntries(careerItems.map((item) => [item.label, item.value])),
  );
  //selected api
  const { qualification, jobSector } = useSelectOptions();
  const { data: incomeData, isLoading: isIncomePending } = useIncomes({
    search: "",
  });
  const selectedQualificationId = formData["Qualification"]?.id || "";
  const { data: courseData, isLoading: isCoursePending } = useCourses({
    search: "",
    parent_id: selectedQualificationId,
  });
  const { data: professionData, isLoading: isProfessionPending } =
    useProfession({
      search: "",
    });
  const selectProfession = mapToSelectOptions(professionData || []);
  const selectCourse = mapToSelectOptions(courseData);
  const selectAnnualIncome = mapToSelectOptions(incomeData);
  const selectCareerOptions = {
    Qualification: qualification?.options,
    "Annual Income": selectAnnualIncome,
    Sector: jobSector?.options,
    Employment: selectEmployment,
    Course: selectCourse,
    Profession: selectProfession,
  };

  useEffect(() => {
    setFormData(
      Object.fromEntries(careerItems.map((item) => [item.label, item.value])),
    );
  }, [careerDetails]);

  const handleChange = (label, value) => {
    setFormData((prev) => {
      const updated = { ...prev };

      if (value?.target) {
        updated[label] = value.target.value;
      } else {
        updated[label] = value;
      }
      if (label === "Qualification") {
        updated["Course"] = null;
      }

      return updated;
    });
  };

  const editProfileMutation = useEditProfile();

  const normalizeValue = (field) => {
    if (!field) return null;
    if (typeof field === "object" && Object.keys(field).length === 0)
      return null;
    if (Array.isArray(field) && field.length === 0) return null;
    return field?.id || field;
  };

  const prepareCareerPayload = () => ({
    qualification: normalizeValue(formData["Qualification"]),
    course: normalizeValue(formData["Course"]),
    company: formData["Company name"] || null,
    sector: normalizeValue(formData["Sector"]),
    employment_type: normalizeValue(formData["Employment"]),
    institution: formData["Institution"] || null,
    occupation: normalizeValue(formData["Profession"]),
    annual_income: normalizeValue(formData["Annual Income"]),
  });

  const handleSave = () => {
    const payload = prepareCareerPayload();

    editProfileMutation.mutate(payload, {
      onSuccess: () => {
        setEditing(false);
        queryClient.invalidateQueries({
          queryKey: ["my-profile"],
        });
      },
    });
  };

  const getOptions = (label) => {
    return careerSelectOptions[label] || null;
  };

  const toggleEdit = () => {
    setEditing((prev) => {
      const enteringEdit = !prev;

      if (enteringEdit) {
        setFormData((prevData) => ({
          ...prevData,
          Institution: careerData?.institution || "",
          "Company name": careerData?.company || "",
        }));
      }

      return !prev;
    });
  };

  return (
    <div id={id} className="career-panel-frame panel-frame">
      <div className="career-details">
        <div className="header flex items-center justify-between">
          <label className="career-label font-title-normal">Career</label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={() => {
                if (editing) {
                  handleSave();
                }
                toggleEdit();

              }}
              className="edit-button flex items-center gap-1"
            >
              {editing ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        <div
          className={`content-grid flex flex-col gap-4 ${isLock && "locked-card"}`}
        >
          {!editing? careerItems.map((item) => {
                const rawValue =
                  item.label === "Employment"
                    ? employmentTypeMap[item.value]
                    : typeof item.value === "object"
                      ? item.value?.name
                      : item.value;

                const hasValue =
                  rawValue !== null &&
                  rawValue !== undefined &&
                  rawValue !== "";
                const shouldBlur = hasValue && blurredField.includes(item.label);
                return (
                  <div
                    key={item.label}
                    className="item-frame flex flex-col gap-1"
                  >
                    <span className="item-label">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`item-value ${shouldBlur ? "blur-text" : ""}`}
                      >
                        {displayTextOrDash(rawValue)}
                      </span>
                      {shouldBlur && (
                        <Tooltip title="You should update your details to see this">
                          <span>
                            <HelpIcon />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })               
            : careerItems.map((item) => {
              const options = getOptions(item.label);
              return (
                <div
                  key={item.label}
                  className="item-frame flex flex-col gap-1"
                >
                  <span className="item-label">{item.label}</span>
                  {options ? (
                    <Select
                      SkeletonHeight={24}
                      className="custom-ant-select"
                      label={""}
                      returnObject
                      value={
                        item.label === "Employment"
                          ? selectCareerOptions[item.label]?.find(
                            (opt) => opt.value === formData[item.label],
                          )
                          : formData[item.label]?.id
                            ? selectCareerOptions[item.label]?.find(
                              (opt) =>
                                opt.value === formData[item.label]?.id,
                            )
                            : selectCareerOptions[item.label]?.find(
                              (opt) => opt.label === formData[item.label],
                            )
                      }
                      options={selectCareerOptions[item.label]}
                      onChange={(val) => handleChange(item.label, val)}
                      inputClassName="item-input-select"
                    />
                  ) : (
                    <Input
                      id={item.label}
                      label={""}
                      value={formData[item.label]}
                      onChange={(val) => handleChange(item.label, val)}
                      inputClassname="item-input"
                    />
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default CareerPanel;
